<template>
    <div class="audit-card card">
        <div v-if="status === states.STATUS_LOADING" class="loading-overlay">
                <span class="icon-wrapper">
                     <span class="fa fa-circle-o-notch fa-2x fa-spin text-info"></span>
                </span>
        </div>
        <audits-modal v-if="displayModal" :properties="selected" :selected="selectedParameterType"
                      @close="displayModal = false">
        </audits-modal>
        <div class="controls">
            <div class="actions d-flex">
                <button class="btn btn-primary" @click="back" :disabled="!canTraverseHistory">
                    <i class="ionicons ion-md-arrow-back"></i>
                </button>
                <button class="btn btn-secondary" @click="reset" :disabled="!canTraverseHistory">
                    <i class="ionicons ion-md-close"></i>
                </button>
                <div class="dropdown">
                    <button class="btn btn-secondary dropdown-toggle" type="button" id="dropdownMenuButton"
                            data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                        <i class="ionicons ion-ios-more"></i>
                    </button>
                    <div class="dropdown-menu" aria-labelledby="dropdownMenuButton">
                        <button class="dropdown-item btn-sm"
                                :class="{active: related}" @click="toggleRelated()">
                            Show Related Audits
                        </button>
                        <button class="dropdown-item btn-sm"
                                :class="{active: displaySql}" @click="displaySql = !displaySql">
                            Show SQL
                        </button>
                        <button class="dropdown-item btn-sm"
                                :class="{active: dev.palette}" @click="dev.palette = !dev.palette">
                            Show Palette
                        </button>
                    </div>
                </div>
                <div class="search-bar">
                    <span class="search-item" :class="{ active: parameters[key] === selected[key]}"
                          v-for="(value, key) in selected" v-if="value">
                        <span class="search-terms" @click="showParameterModal(key)">
                            <span class="search-type">{{formatParameterType(key)}}</span>
                            <span class="search-value">{{formatParameterValue(value)}}</span>
                        </span>
                        <span class="search-remove" @click="selected[key] = null">&times;</span>
                    </span>
                    <button v-if="!selected.empty()" class="search-clear" @click="selected.reset()">
                        <i class="ionicons ion-md-backspace"></i>
                    </button>
                </div>
                <button class="btn btn-secondary" @click="toggleParameterModal()">
                    <i class="ionicons ion-md-add"></i>
                </button>
                <button class="btn btn-primary" @click="search" :disabled="!canSearch">
                    <i class="ionicons ion-md-search"></i>
                </button>
            </div>
        </div>
        <div v-if="dev.sql && displaySql" class="p-2 dev-sql">
            {{ dev.sql }}
        </div>
        <div v-if="dev.palette" class="dev-palette-viewer">
            <div v-for="color of theme.palette" :style="{ background: '#'+color }" class="color">
                {{ color }}
            </div>
        </div>
        <div class="audit-list-container">
            <div class="scroll-indicator" :class="{ active: indicators.scroll}">
                <i class="icon ionicons ion-md-arrow-down"></i>
            </div>
            <div class="audit-list" ref="auditList" @scroll="updateIndicator">
                <table class="table">
                    <thead>
                    <tr>
                        <th style="width: 1px">{{ 'audit.columns.timestamp' | lang }}</th>
                        <th style="width: 1px">{{ 'audit.columns.user' | lang }}</th>
                        <th style="width: 1px">{{ 'audit.columns.event' | lang }}</th>
                        <th style="width: 1px">{{ 'audit.columns.subject' | lang }}</th>
                        <th>{{ 'audit.columns.route' | lang }}</th>
                    </tr>
                    </thead>
                    <tbody>
                    <template v-for="audit in audits">
                        <tr class="audit-row">
                            <td>
                                {{ audit.created_at }}
                            </td>
                            <td>
                                <template v-if="audit.user_type && audit.user_id">
                                    <audit-selectable class="model user"
                                                      :title="audit.user_type+' #'+audit.user_id"
                                                      :properties="{ user_id: audit.user_id, user_type: audit.user_type }">
                                        <template v-if="audit.user">
                                            <div class="name">{{audit.user.name}}</div>
                                        </template>
                                        <template v-else>
                                            <div class="type">{{formatParameterValue(audit.user_type)}}</div>
                                            <div class="id">#{{audit.user_id}}</div>
                                        </template>
                                    </audit-selectable>
                                </template>
                                <template v-else>
                                    <div class="model system">
                                        <div class="type">
                                            {{ 'audit.model.system' | lang }}
                                        </div>
                                    </div>
                                </template>
                            </td>
                            <td>
                                <audit-selectable class="text-capitalize" :properties="{event: audit.event}">
                                    {{audit.event}}
                                </audit-selectable>
                            </td>
                            <td>
                                <audit-selectable class="model subject"
                                                  :properties="{ auditable_id: audit.auditable_id, auditable_type: audit.auditable_type }"
                                                  :title="audit.auditable_type+' #'+audit.auditable_id"
                                                  :style="{ background: getTypeColor(audit.auditable_type)}">
                                    <template v-if="audit.auditable && audit.auditable.hasOwnProperty('name')">
                                        <div class="name">{{audit.auditable.name}}</div>
                                    </template>
                                    <template v-else>
                                        <div class="info">
                                            <div class="type">{{formatParameterValue(audit.auditable_type)}}</div>
                                            <div class="id">#{{audit.auditable_id}}</div>
                                        </div>
                                    </template>
                                </audit-selectable>
                            </td>
                            <td>
                                <div class="small">{{audit.url}}</div>
                            </td>
                        </tr>
                        <tr class="change-row">
                            <td colspan="100%">
                                <ul class="audit-keys">
                                    <li v-for="(value, key) in audit.new_values"
                                        :class="{ active: key === selected.column }">
                                        {{ 'audit.column' | lang }}
                                        <audit-selectable class="key text-uppercase d-inline"
                                                          :properties="{column: key}">{{key}}
                                        </audit-selectable>
                                        <template v-if="audit.old_values.hasOwnProperty(key)">
                                            {{ 'audit.changed' | lang }} <span class="value old">“{{audit.old_values[key]}}”</span>
                                            {{ 'audit.to' | lang }}
                                        </template>
                                        <template v-else> {{ 'audit.set' | lang }}</template>
                                        <span class="value">{{formatColumnValue(value)}}</span>
                                    </li>
                                </ul>
                            </td>
                        </tr>
                    </template>
                    </tbody>
                </table>
            </div>
        </div>
        <div class="py-2 d-flex justify-content-between align-items-center">
            <div class="pagination">
                <paginate v-model="page"
                          :page-count="totalPages"
                          :click-handler="setPage"
                          containerClass="pagination"
                          page-class="page-item"
                          prev-class="page-item"
                          next-class="page-item"
                          page-link-class="page-link"
                          prev-link-class="page-link"
                          next-link-class="page-link">
                </paginate>
                <div class="page-item" v-if="totalPages > 1">
                    <a @click="selectPage" href="#" class="page-link"><i class="ionicons ion-md-search"></i></a>
                </div>
            </div>
            <div class="px-3" v-if="info">
                <small>Showing {{info.from}} - {{info.to}} of {{info.total}} logs</small>
            </div>
        </div>
    </div>
</template>

<script>
  import axios from 'axios';
  import Paginate from 'vuejs-paginate'
  import AuditsModal from './AuditsModal.vue'
  import AuditParameters from './AuditParameters';
  import AuditTheme from './AuditTheme';
  import AuditSelectable from './AuditSelectable';

  const queryString = require('query-string');

  export default {
    name: "AuditsList",
    components: {
      'paginate': Paginate,
      'audits-modal': AuditsModal,
      'audit-selectable': AuditSelectable,
    },
    props: {
      'initialPage': Number,
      'length': Number,
      'type': String,
      'id': Number,
      'userType': String,
      'userId': Number,
      'relations': Boolean,
      'queryParameters': Boolean,
    },
    data() {
      return {
        states: {
          STATUS_EMPTY: 0,
          STATUS_LOADING: 1,
          STATUS_FAILED: 2,
          STATUS_LOADED: 3
        },
        status: 0,
        info: {},
        dev: {
          sql: '',
          palette: false,
        },
        displaySql: false,
        audits: [],
        page: 1,
        related: false,
        theme: new AuditTheme(),
        statics: new AuditParameters(),
        parameters: new AuditParameters(),
        selected: new AuditParameters(),
        displayModal: false,
        selectedParameterType: null,
        parameterHistory: [new AuditParameters()],
        indicators: {
          scroll: false,
        },
      }
    },
    computed: {
      canTraverseHistory() {
        return this.parameterHistory.length > 1;
      },
      canSearch() {
        for (const key in this.selected) {
          if (!this.selected.hasOwnProperty(key) || !this.parameters.hasOwnProperty(key)) {
            continue;
          }

          const current = this.parameters[key];
          const selected = this.selected[key];

          if (current !== selected) {
            return true;
          }

        }
        return false;
      },
      currentPage() {
        return parseInt(this.page);
      },
      pageOptions() {
        let pages = [];
        for (let index = 1; index <= this.totalPages; index++) {
          pages.push({
            number: index,
            active: index === this.currentPage,
          });
        }
        return pages;
      },
      totalPages() {
        return parseInt(this.info.last_page) || 1;
      },
    },
    methods: {
      getTypeColor(str) {
        return this.theme.colorFromType(this.formatParameterValue(str)) || 'inherit';
      },
      toggleRelated() {
        this.related = !this.related;
        this.page = 1;
        this.get();
      },
      formatParameterType(value) {
        return value.toString().replace(/_/g, ' ');
      },
      formatParameterValue(value) {
        return value.toString().replace(/.*\\/, '')
      },
      formatColumnValue(value, stop = 512) {
        if (value === null) {
          return 'null'
        }

        if (typeof value === 'string') {
          const text = value.slice(0, stop).trimEnd() + (stop < value.length ? '...' : '');
          return `“${text}”`;
        }

        return value.toString();
      },
      toggleParameterModal() {
        this.selectedParameterType = '';
        this.displayModal = !this.displayModal;
      },
      showParameterModal(parameterType = '') {
        this.selectedParameterType = parameterType;
        this.displayModal = true;
      },
      saveHistory() {
        // Current parameters (without observer)
        const parameters = JSON.parse(JSON.stringify(this.parameters));

        // Save parameters to history array.
        this.parameterHistory.push(parameters);
      },
      search() {
        if (!this.canSearch) {
          return;
        }

        for (const key in this.selected) {
          if (this.selected.hasOwnProperty(key)) {
            this.parameters.set(key, this.selected[key]);
          }
        }
        this.saveHistory();
        this.page = 1;

        this.get();
      },
      isSelectable(propertyKeys) {
        for (const key of propertyKeys) {
          if (!this.selected.hasOwnProperty(key)) {
            return false;
          }

          // Skip selection if static parameters are already set.
          if (this.statics.exists(key)) {
            return false;
          }
        }
        return true;
      },
      select(properties) {
        if (!this.isSelectable(Object.keys(properties))) {
          return;
        }

        let unset = false;
        let onset = false;

        for (const key in properties) {
          if (!properties.hasOwnProperty(key)) {
            continue;
          }

          const currentValue = this.selected.get(key);
          let value = properties[key];

          // Set all subsequent properties to null if this is a deselect action (basically makes selection a toggle)
          if ((currentValue === value || unset) && !onset) {
            value = null;
            unset = true;
          } else {
            onset = true;
          }

          this.selected.set(key, value);
        }
      },
      clean(obj) {
        for (const propName in obj) {
          if (!obj.hasOwnProperty(propName)) {
            continue;
          }
          if (obj[propName] === null || obj[propName] === undefined) {
            delete obj[propName];
          }
        }
      },
      reset() {
        this.parameterHistory = [new AuditParameters()];
        this.parameters.reset();
        this.selected.reset();
        this.page = 1;
        this.get();
      },
      back() {
        // Remove current settings from history
        this.parameterHistory.pop();

        // Get remaining length
        const length = this.parameterHistory.length;

        // Reset if not enough history is available.
        if (length < 1) {
          this.reset();
          return;
        }

        // Get latest history parameters
        const previous = this.parameterHistory[length - 1];
        this.parameters = new AuditParameters(Object.assign({}, previous));
        this.selected = new AuditParameters(Object.assign({}, previous));
        this.page = 1;
        this.get();
      },
      next() {
        this.setPage(this.page + 1);
      },
      prev() {
        this.setPage(this.page - 1);
      },
      setPage(number) {
        number = Math.max(Math.min(this.info.last_page || 1, number), 1);
        this.page = number;
        this.get();
      },
      selectPage(e) {
        swal({
          text: "Input page number",
          closeOnClickOutside: false,
          content: {
            element: "input",
            attributes: {
              placeholder: this.page,
              type: "number",
              class: "form-control",
            },
          },
        }).then((n) => {
          if (!isNaN(parseFloat(n)) && isFinite(n)) {
            this.setPage(n);
          }
        });
      },
      isEmpty(value) {
        return value === "" || value === null;
      },
      get() {
        this.status = this.states.STATUS_LOADING;
        const parameters = JSON.parse(JSON.stringify(this.parameters));

        // Set static values (constant un-editable parameters)
        for (const key in this.statics) {
          if (!this.statics.hasOwnProperty(key) || !this.statics.exists(key)) {
            continue;
          }

          parameters[key] = this.statics[key];
        }

        parameters.page = this.page;
        parameters.related = this.related ? 1 : null;
        this.saveQueryParams(parameters);

        axios.get('/admin/audits', {
          params: parameters,
          headers: {
            'Content-Type': 'application/json'
          }
        }).then(response => {
          const data = response.data;
          const info = data.table;
          this.dev.sql = data.sql.replace(/\\/g, '\\\\');

          if (info.data) {
            let audits = [];
            for (const audit of info.data) {
              audit.toggled = true;
              this.clean(audit.old_values);
              audits.push(audit);
            }

            this.audits = audits;
          }
          this.info = info;
        }).catch((error) => {
          swal(error.message || Lang.get('audit.error.get'), {icon: 'error'});
        }).finally(() => {
          this.$refs.auditList.scrollTop = 0;
          this.status = this.states.STATUS_LOADED;
          this.updateIndicator({target: this.$refs.auditList});
        });
      },
      updateIndicator(event) {
        const target = event.target;
        const height = target.scrollHeight - target.clientHeight;
        this.indicators.scroll = target.scrollTop < height;
      },
      loadQueryParams() {
        this.parameterHistory.push(new AuditParameters);

        const parameters = queryString.parse(location.search);

        if (parameters.page || false) {
          this.page = parseInt(parameters.page);
        }

        for (const key in parameters) {
          const value = parameters[key] || null;
          this.parameters.set(key, value);
          this.selected.set(key, value);
        }

        this.get();
      },
      saveQueryParams(parameters) {
        if (!this.queryParameters || !window.history) {
          return;
        }

        // Remove null params
        Object.keys(parameters).forEach((key) => (parameters[key] == null) && delete parameters[key]);

        const url = window.location.protocol + "//" + window.location.host + window.location.pathname;
        const query = queryString.stringify(parameters);
        window.history.replaceState(null, null, `${url}?${query}`);
      },
    },
    mounted() {
      this.page = this.initialPage;
      this.related = this.relations || false;
      this.statics.auditable_type = this.type;
      this.statics.auditable_id = this.id;

      // Sync parameters with page.
      if (this.queryParameters) {
        this.loadQueryParams();

        return;
      }

      this.reset();
    }
  }
</script>